import { useEffect, createContext, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Create a context to manage the navigation blocker state
export const NavigationBlockerContext = createContext();

export const useNavigationBlocker = () => {
  const context = useContext(NavigationBlockerContext);
  if (!context) {
    throw new Error('useNavigationBlocker must be used within a NavigationBlockerProvider');
  }
  return context;
};

export const NavigationBlockerProvider = ({ children }) => {
  const [isBlocked, setIsBlocked] = useState(true);

  return (
    <NavigationBlockerContext.Provider value={{ isBlocked, setIsBlocked }}>
      <NavigationBlocker />
      {children}
    </NavigationBlockerContext.Provider>
  );
};

const NavigationBlocker = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isBlocked } = useNavigationBlocker();

  useEffect(() => {
    if (!isBlocked) return; // Skip if blocking is disabled

    const preventNavigation = (event) => {
      // Cancel the event
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
    };

    const blockBrowserNavigation = (event) => {
      if (event.type === 'popstate') {
        // Push the current route again to prevent navigation
        navigate(location.pathname, { replace: true });
      }
    };

    // Add event listeners
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', blockBrowserNavigation);
    window.addEventListener('beforeunload', preventNavigation);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', blockBrowserNavigation);
      window.removeEventListener('beforeunload', preventNavigation);
    };
  }, [navigate, location, isBlocked]);

  return null;
};

export default NavigationBlocker;
